import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { trackFormStart, trackGenerateLead } from "../../lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AgencyContactForm() {
  const [form, setForm] = useState({ name: "", phone: "", company: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [touched, setTouched] = useState(false);

  const update = (key) => (e) => {
    if (!touched) {
      trackFormStart("agency_final");
      setTouched(true);
    }
    setForm((f) => ({ ...f, [key]: e.target.value }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const next = {};
    if (!form.name.trim()) next.name = "Podaj imię";
    const cleaned = form.phone.replace(/[\s\-\(\)]/g, "");
    if (!/^(\+48)?\d{9}$/.test(cleaned)) next.phone = "Wpisz poprawny numer (9 cyfr)";
    if (!form.company.trim()) next.company = "Podaj nazwę firmy";

    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/callback`, {
        phone: cleaned,
        page: "AGENCY",
        source: "Final",
        name: form.name.trim(),
        company: form.company.trim(),
        url: typeof window !== "undefined" ? window.location.href : null,
      });
      trackGenerateLead("agency_final");
      setSuccess(true);
      setForm({ name: "", phone: "", company: "" });
      toast.success("Dzięki! Odezwiemy się w ciągu kilku godzin.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(", ")
        : detail || "Coś poszło nie tak. Spróbuj ponownie.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div
        className="rounded-3xl bg-white/[0.05] ring-1 ring-emerald-400/30 p-8 sm:p-10"
        data-testid="agency-form-success"
      >
        <div className="flex items-start gap-4">
          <div className="grid place-items-center h-12 w-12 rounded-full bg-emerald-500 text-white flex-shrink-0">
            <CheckCircle2 className="h-6 w-6" />
          </div>
          <div>
            <p className="font-heading font-bold text-white text-2xl tracking-tight">
              Dzięki, zgłoszenie poszło.
            </p>
            <p className="mt-2 text-white/65 leading-relaxed">
              Odezwiemy się w ciągu kilku godzin z propozycją terminu rozmowy.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      data-testid="agency-contact-form"
      className="rounded-3xl bg-white/[0.04] ring-1 ring-white/10 p-6 sm:p-8 lg:p-10 backdrop-blur-sm"
    >
      <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
        <Field
          id="agency-name"
          label="Imię"
          value={form.name}
          onChange={update("name")}
          placeholder="Jan"
          error={errors.name}
          autoComplete="given-name"
          testId="agency-name"
        />
        <Field
          id="agency-phone"
          label="Telefon"
          value={form.phone}
          onChange={update("phone")}
          placeholder="600 000 000"
          error={errors.phone}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          testId="agency-phone"
        />
        <div className="sm:col-span-2">
          <Field
            id="agency-company"
            label="Firma"
            value={form.company}
            onChange={update("company")}
            placeholder="Nazwa firmy"
            error={errors.company}
            autoComplete="organization"
            testId="agency-company"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="agency-form-submit"
        className="mt-7 w-full h-14 rounded-full bg-brand-yellow hover:bg-brand-yellowHover text-brand-yellowFg font-bold text-base shadow-lg hover:shadow-xl active:scale-[0.98] transition-all disabled:opacity-70 inline-flex items-center justify-center"
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Wysyłanie…
          </>
        ) : (
          <>
            Umów rozmowę
            <ArrowRight className="ml-2 h-5 w-5" />
          </>
        )}
      </button>

      <div className="mt-4 flex items-start gap-2 text-xs sm:text-sm text-white/55">
        <ShieldCheck className="h-4 w-4 text-brand-yellow flex-shrink-0 mt-0.5" />
        <span>
          Bez spamu. Bez zobowiązań.
          <br className="sm:hidden" />
          <span className="hidden sm:inline"> </span>
          Odpowiadamy w kilka godzin.
        </span>
      </div>
    </form>
  );
}

function Field({ id, label, value, onChange, placeholder, error, type = "text", autoComplete, inputMode, testId }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-xs font-semibold uppercase tracking-wider text-white/60 mb-2"
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        inputMode={inputMode}
        autoComplete={autoComplete}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={`${testId}-input`}
        className={`w-full h-13 sm:h-14 px-4 rounded-xl bg-white/5 ring-1 ${
          error ? "ring-red-400/60" : "ring-white/10 focus:ring-brand-yellow/60"
        } text-white placeholder:text-white/35 text-base focus:outline-none focus:bg-white/[0.07] transition-all`}
        style={{ height: 56 }}
      />
      {error && (
        <p className="mt-2 text-xs text-red-300" data-testid={`${testId}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}
